/**
 * Create Mode — registers the 'create' router mode and orchestrates dialog sub-modules.
 *
 * Sub-modules under modules/create/dialog/:
 *   dialog-shell.js   — HTML scaffold + source/result/upload/lightbox bindings
 *   form-modes.js     — input_mode-aware renderer (image / text / image+text / client_edit)
 *   dispatch-bridge   — single dispatchFromUi(intent, jobInput) entry point (Phase 2)
 *
 * Sibling modules:
 *   client-handlers/registry.js — F-class handlers (Phase 5, populated by H-031)
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { registerMode, setMode, setRightPanelTab } from '../../core/router.js'
import { showToast } from '../../utils/toast.js'
import { initIntentCards } from './cards/intent-cards.js'
import {
  renderInspirationWorkspace,
  getCustomStylePrompt,
  addReferenceCards,
} from '../create-ui/inspiration-workspace.js'
import { initNegativePromptCard, getNegativePrompt } from '../create-ui/negative-prompt-card.js'
import { initInspirationWall } from '../create-ui/inspiration-wall.js'
import {
  initDialogShell,
  activateShell,
  deactivateShell,
  onGenerateClick,
  showResultImage,
  setResultLoading,
  setResultError,
  resolveInputMode,
  renderPlays,
} from './dialog/dialog-shell.js'
import { dispatchFromUi } from './dialog/dispatch-bridge.js'
import { requireAuth } from '../auth/index.js'
import { initMockupPreview } from './dialog/mockup-preview.js'
import {
  getSourceSlotRef,
  getReferenceSlotRef,
  materializeExternalSource,
} from '../create-ui/image-roles.js'
import { createLogger } from '../../utils/logger.js'
import {
  initStudio,
  activate as activateStudio,
  deactivate as deactivateStudio,
} from '../studio/index.js'
const log = createLogger('create')

let initialized = false
// Set when a generate was blocked by the login gate, so it auto-resumes after
// the user signs in (instead of the user having to click Generate again).
let _pendingGenerate = false

function activate() {
  initStudio()
  activateStudio()
  const container = document.getElementById('legacy-create-shell')
  if (!container) return

  if (!initialized) {
    initDialogShell(container)
    onGenerateClick(handleGenerate)
    initMockupPreview()

    // Resume a generation that was blocked by the login gate, once the user
    // signs in via the modal. why → "I logged in but nothing generated" gap.
    eventBus.on(EVENTS.USER_LOGIN, () => {
      if (_pendingGenerate) {
        _pendingGenerate = false
        handleGenerate()
      }
    })

    initialized = true

    initIntentCards()
      .then((cards) => {
        // Actions = intent cards, grouped by pipeline metadata (input_mode /
        // requires_second_image) in the left toolbar of the A+B->C workspace.
        // why → ADR-005 2.6.0 (frontend never branches by backend model).
        renderPlays(cards)
      })
      .catch((e) => log.warn('[Create] Intent cards load failed:', e))

    renderInspirationWorkspace()
    initNegativePromptCard().catch((e) => log.warn('[Create] Negative cards load failed:', e))

    // Inspiration (the image's vision-extracted prompt cards) lives in the docked
    // right-panel. Reveal its tab in create mode, and surface it the moment a
    // source image is read — the cards ARE the prompt. why → image-is-the-prompt.
    eventBus.on(EVENTS.VISION_COMPLETE, (result) => {
      // The A source image's vision cards ARE the prompt — push them into the
      // inspiration store so the right-panel shows them. why → image-is-the-prompt.
      // Origin 'source': a NEW source's bundle REPLACES the previous source's
      // cards; only reference-fed cards accumulate. why → ADR-005 #11.
      const cards = result?.cards || result?.data?.cards || []
      if (cards.length) addReferenceCards(cards, 'source')
      renderInspirationWorkspace()
      setRightPanelTab('inspiration')
    })

    // Funnel: a finished result is handed off to the product editor so the user
    // puts it on a product and ORDERS it — the only revenue step is the purchase
    // (creator resale/earnings = future, unbuilt). why → commercial-model §1/§5.
    eventBus.on(EVENTS.PUT_ON_PRODUCT, ({ url } = {}) => {
      // Carry the generated design INTO the product editor via the same inject
      // path Gallery->Design uses (design.injectImageUrl), so the user lands on a
      // product WITH their design — not an empty editor. The handler previously
      // dropped the {url} payload and only switched mode, breaking the pipeline
      // handoff (generate -> put on product -> order). why → bao 2026-06-26.
      if (url) store.setState('design.injectImageUrl', url)
      showToast('Taking this design into the product editor…', 'success', 1800)
      setMode('design')
    })
  }

  activateShell()

  // Create opens with the Inspiration (image-prompt) panel as the right column —
  // tab visibility + default are owned by router.js (MODE_TABS, ADR-005 2.8.0 #3).
  // Cold start shows the official seed WALL (H-2026-09-12-128 2a) until an
  // image's vision cards take over the panel (renderInspirationWorkspace
  // toggles between the two).
  initInspirationWall()
  renderInspirationWorkspace()
}

function deactivate() {
  deactivateStudio()
  deactivateShell()
}

// ---- Generate ---------------------------------------------------------

async function handleGenerate() {
  // Not logged in → open the existing login modal instead of letting the request
  // dump a raw 401. Remember the intent so generation auto-resumes after sign-in
  // (USER_LOGIN handler below) — the user shouldn't have to click Generate twice.
  if (!requireAuth()) {
    _pendingGenerate = true
    return
  }

  // Re-entrancy guard — rebindGenerate clones a fresh ENABLED button on every
  // mode switch, and the vision-poll wait below runs before the old code locked
  // `generating`, so a second click could fire a concurrent generation. why → audit P1#4.
  if (store.getState('creation.generating')) return

  // Prompt-first: typing + Generate with NO action selected = plain text-to-image.
  // Synthesize a default text intent so the user is never blocked at the door —
  // picking an ACTION is for EDITING an existing image, not a prerequisite to create.
  // why -> prompt-first main path was dead-locked behind intent selection 2026-06-22.
  let intentCard = store.getState('creation.intentCard')
  if (!intentCard) {
    // execution_mode 'ideogram' = text-to-image with NO source image (fal-ai/ideogram/v3).
    // 'prompt'/'hybrid' are img2img and would 422 "requires a source image". Verified
    // live end-to-end (12c, 11.3s, real PNG). why -> prompt-first text-to-image path.
    intentCard = {
      id: null,
      execution_mode: 'ideogram',
      input_mode: 'text',
      title: 'Text to Image',
      requires_second_image: 0,
    }
  }

  const validation = validateJobInput(intentCard)
  if (!validation.ok) {
    showToast(validation.error, 'error')
    return
  }

  // Lock BEFORE the vision-poll wait so the re-entrancy guard covers it; drop the
  // previous result so a stale success can't linger as a mockup tile. why → audit P1#3.
  store.setState('creation.generating', true)
  store.setState('creation.result', null)
  const generateBtn = document.getElementById('generateBtn')
  if (generateBtn) generateBtn.disabled = true

  try {
    // An injected/external source (?injectImage funnel) has a URL but no typed
    // slot ref — the generate input contract rejects raw source URLs (SSRF,
    // ADR-005 #10), so it would 4xx as an opaque "Generation failed". Lazily
    // materialize it into a real asset via the existing two-step upload (same
    // compliance path as a file upload), then generate with the typed ref.
    const src = store.getState('sourceImage')
    if (
      resolveInputMode(intentCard) !== 'text' &&
      src?.url &&
      !getSourceSlotRef() &&
      /^https?:\/\//.test(src.url)
    ) {
      setResultLoading('Preparing your image…')
      try {
        await materializeExternalSource(src.url)
      } catch (err) {
        log.error('external source materialization failed:', err)
        throw new Error(
          'Could not load this image from its site — save it and upload it here instead'
        )
      }
    }

    const jobInput = buildJobInput(intentCard)

    if (store.getState('creation.visionLoading') && !store.getState('creation.visionAnalysis')) {
      setResultLoading('Analyzing image...')
      const startedAt = Date.now()
      while (store.getState('creation.visionLoading') && Date.now() - startedAt < 8000) {
        await new Promise((r) => setTimeout(r, 200))
      }
      jobInput.vision_analysis = store.getState('creation.visionAnalysis') || null
    }

    const result = await dispatchFromUi(intentCard, jobInput, {
      onProgress: setResultLoading,
    })

    if (!result.success) {
      throw new Error(result.error || 'Dispatch failed')
    }

    const url = result.image_url || result.data_url
    if (!url) throw new Error('Dispatcher returned no image URL')

    // Collect every card ULID consumed by this generation so the Keep/Discard
    // buttons can emit per-card adopt/discard signals to creator-api.
    const cardIds = [intentCard?.id, ...(jobInput.style_card_ids || [])].filter(Boolean)
    showResultImage(url, result.generation_id || null, cardIds, result.asset_id || null)
    showToast(result.kind === 'client' ? 'Done!' : 'Generation complete!', 'success')
    eventBus.emit(EVENTS.GENERATION_COMPLETE, {
      url,
      jobId: result.generation_id || null,
      kind: result.kind,
    })
  } catch (err) {
    log.error('[Create] Generation failed:', err)
    setResultError(err.message)
    showToast('Generation failed', 'error')
  } finally {
    store.setState('creation.generating', false)
    if (generateBtn) generateBtn.disabled = false
  }
}

// H-167: the external-source materialization (fetch → two-step upload → typed
// asset ref) moved to create-ui/image-roles.js so the studio gallery shares it.

function validateJobInput(intentCard) {
  const mode = resolveInputMode(intentCard)
  const sourceUrl = store.getState('sourceImage')?.url || null
  const textInput = (store.getState('creation.textInput') || '').trim()

  if (mode === 'image' && !sourceUrl) {
    return { ok: false, error: 'Please upload or select a source image first' }
  }
  if (mode === 'image+text') {
    if (!sourceUrl) return { ok: false, error: 'Please upload or select a source image first' }
    if (!textInput) return { ok: false, error: 'Please describe the transformation' }
  }
  if (mode === 'text' && !textInput) {
    return { ok: false, error: 'Please describe what to generate' }
  }
  if (mode === 'client_edit' && !sourceUrl) {
    return { ok: false, error: 'Please upload or select a source image to edit' }
  }
  // A reference can come from the intent-card upload (secondImageUrl) OR the
  // gallery "use as reference" button (referenceSlot). Either satisfies the
  // requires_second_image contract. why → ADR-005 2.4.1.
  if (
    intentCard?.requires_second_image &&
    !store.getState('creation.secondImageUrl') &&
    !store.getState('referenceSlot')?.url
  ) {
    return {
      ok: false,
      error:
        'Add a reference image (upload on the intent card or "Use as reference" in the gallery)',
    }
  }
  return { ok: true }
}

function buildJobInput(intentCard) {
  const mode = resolveInputMode(intentCard)
  const sourceUrl = store.getState('sourceImage')?.url || null
  // The reference pixel is sent for any image-mode intent. The BACKEND decides
  // how to consume it via requires_second_image: two-image intents (Model Swap /
  // Try-On) fuse it pixel-to-pixel through flux-pro/kontext/multi; single-image
  // edits (Colorway etc.) extract its colour/style via vision into {{target}}
  // so it guides the edit without echoing. Before this, Colorway silently
  // dropped a user-picked reference, leaving {{target}} empty and the model
  // echoing the source ("result = original"). why → colorway fix 2026-07-26.
  const wantsReferencePixel = mode !== 'text'
  const textInput = (store.getState('creation.textInput') || '').trim()
  const promptInputEl = document.getElementById('promptInput')
  // Merge the free-text "Custom" inspiration card into the prompt — it was
  // silently dropped before (getCustomStylePrompt was never called). dead-wire fix.
  const customPrompt = [promptInputEl?.value?.trim(), getCustomStylePrompt()]
    .filter(Boolean)
    .join(', ')

  const inspirationCards = store.getState('inspirationCards')
  const checkedCards = inspirationCards?.checkedCards || {}
  const styleCardIds = (inspirationCards?.cards || [])
    .filter((card) => checkedCards[card.id])
    .map((card) => card.id)
  // Per-card strength (ADR-005 2.5.0) — only checked cards, only when not the
  // default 'mid' (the backend treats absent as verbatim). why → ADR-005 2.5.0.
  const cardStrengthsMap = inspirationCards?.cardStrengths || {}
  const card_strengths = {}
  styleCardIds.forEach((id) => {
    const lv = cardStrengthsMap[id]
    if (lv && lv !== 'mid') card_strengths[id] = lv
  })

  return {
    source_image_url: mode === 'text' ? null : sourceUrl,
    inspiration_image_url: mode === 'text' ? sourceUrl : null,
    reference_image_url: wantsReferencePixel
      ? store.getState('creation.secondImageUrl') || null
      : null,
    // Typed source-slot ref { origin, id } (ADR-005 #10) — generate re-validates
    // + size-resolves it server-side. Supersedes the deprecated reference_asset_id;
    // text-mode has no pixel source. The slot was filled by useImageAsSource
    // (gallery library = origin 'library') or a fresh upload (origin 'asset').
    source_ref: mode === 'text' ? null : getSourceSlotRef(),
    // Typed REFERENCE ref (the 2nd pixel) for two-image intents (Style Transfer
    // / Try-On / Model Swap). ADR-005 2.4.1 / #10. Two entry points, unified:
    //  - gallery "use as reference" button → referenceSlot (library ref)
    //  - intent-card "upload reference"   → creation.referenceAssetId (asset)
    reference_ref: wantsReferencePixel
      ? getReferenceSlotRef() ||
        (store.getState('creation.referenceAssetId')
          ? { origin: 'asset', id: String(store.getState('creation.referenceAssetId')) }
          : null)
      : null,
    // asset id from the two-step upload — kept only as a DEPRECATED fallback for
    // an uploaded source that did not record a typed ref. why → ADR-005 #2/#10.
    reference_asset_id: wantsReferencePixel
      ? store.getState('creation.referenceAssetId') || null
      : null,
    text_input: textInput || null,
    custom_prompt: customPrompt || null,
    aspect_ratio: store.getState('aspectRatio') || '3:4',
    vision_analysis: store.getState('creation.visionAnalysis') || null,
    style_card_ids: styleCardIds,
    card_strengths,
    // Negative cards composed live — creation.negativePrompt was never written
    // (getNegativePrompt never called), so the whole Negative column did nothing. fix.
    negative_prompt: getNegativePrompt() || null,
  }
}

registerMode('create', { activate, deactivate })
